import { useController, type Control, type FieldValues, type Path } from 'react-hook-form';
import { NativeSelect, Select, TextInput } from '@mantine/core';
import { CountryCodes } from '@lib2/model/form/CountryCodes';

/** Country options for the billing address Select (shared by both card forms). */
export const COUNTRY_OPTIONS = CountryCodes.ISO2Labels.map(([value, label]) => ({ value, label }));

/** Generic controlled TextInput for RHF + Mantine. */
export function ControlledTextInput<T extends FieldValues>({
  control,
  name,
  label,
  autoComplete,
  maxLength,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  autoComplete?: string;
  maxLength?: number;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <TextInput
      ref={field.ref}
      name={field.name}
      label={label}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      autoComplete={autoComplete}
      maxLength={maxLength}
      withAsterisk={required}
    />
  );
}

/** Generic controlled Select for RHF + Mantine. */
export function ControlledSelect<T extends FieldValues>({
  control,
  name,
  label,
  data,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  data: { value: string; label: string }[];
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <Select
      ref={field.ref}
      label={label}
      data={data}
      value={String(field.value ?? '')}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      searchable
      withAsterisk={required}
    />
  );
}

/** Controlled native <select> for numeric fields — required for browser card-expiry autofill. */
export function ControlledNativeSelect<T extends FieldValues>({
  control,
  name,
  label,
  data,
  autoComplete,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  data: { value: string; label: string }[];
  autoComplete?: string;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <NativeSelect
      ref={field.ref}
      name={field.name}
      label={label}
      data={data}
      value={String(field.value ?? '')}
      onChange={(event) => field.onChange(Number(event.currentTarget.value))}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      autoComplete={autoComplete}
      withAsterisk={required}
    />
  );
}
